// "Ferske minner": a carousel of photos from events in the last six months, newest first.
// Shows 4 at a time (2 on phones) and glides one photo to the left every 2 seconds, in a seamless loop.
// Each photo is marked with the event it is from, and links to the trip (blåtur) or the gallery.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { app } from './firebase-config';
import { useAuthUser, BLATUR_TYPE } from './blaturData';
import { tripTitle, tripCover } from './tripHelpers';
import { typeInfo, quarterEndDate } from './eventTypes';
import { SmartImage } from './imageUtils';

const db = getFirestore(app);
const MONTHS_BACK = 6;
const PER_EVENT = 12;
const MAX_PHOTOS = 40;
const INTERVAL_MS = 2000;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const place = (event) => (event.event_type === BLATUR_TYPE ? tripTitle(event) : (event.sted_konsept || '').trim());

function FerskeMinner() {
  const user = useAuthUser();
  const [events, setEvents] = useState(null);
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const resumeTimer = useRef(null);

  useEffect(() => {
    if (!user) return;
    getDocs(collection(db, 'events'))
      .then(snap => setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(error => {
        console.error('Error fetching recent events: ', error);
        setEvents([]);
      });
  }, [user]);

  const photos = useMemo(() => {
    if (!events) return [];
    const now = new Date();
    const since = new Date(now.getFullYear(), now.getMonth() - MONTHS_BACK, now.getDate());
    const recent = events
      .filter(e => (e.images || []).length > 0)
      .map(e => ({ ...e, end: quarterEndDate(e.Kvartal) }))
      .filter(e => e.end && e.end >= since && (e.Kvartal || '') <= `${now.getFullYear()} Q${Math.floor(now.getMonth() / 3) + 1}`)
      .sort((a, b) => (b.Kvartal || '').localeCompare(a.Kvartal || ''));

    const list = [];
    for (const event of recent) {
      const cover = tripCover(event);
      const images = [cover, ...event.images.filter(url => url !== cover)].slice(0, PER_EVENT);
      images.forEach(url => list.push({ url, event }));
    }
    return list.slice(0, MAX_PHOTOS);
  }, [events]);

  // The list is rendered twice, so stepping past the end can jump back invisibly to the same photo
  const loop = photos.length > 4;
  const slides = loop ? [...photos, ...photos] : photos;

  const step = useCallback((direction) => {
    const track = trackRef.current;
    if (!track || track.children.length < 2) return;
    const width = track.children[1].offsetLeft - track.children[0].offsetLeft;
    let index = Math.round(track.scrollLeft / width);
    if (loop && direction > 0 && index >= photos.length) {
      index -= photos.length;
      track.scrollTo({ left: index * width, behavior: 'instant' });
    }
    if (loop && direction < 0 && index <= 0) {
      index += photos.length;
      track.scrollTo({ left: index * width, behavior: 'instant' });
    }
    track.scrollTo({ left: (index + direction) * width, behavior: 'smooth' });
  }, [loop, photos.length]);

  useEffect(() => {
    if (!loop || paused || reducedMotion()) return undefined;
    const id = setInterval(() => {
      if (!document.hidden) step(1);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [loop, paused, step]);

  // Touch: pause while the user swipes, then continue a little later
  const pauseForTouch = () => {
    setPaused(true);
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => setPaused(false), 6000);
  };
  useEffect(() => () => clearTimeout(resumeTimer.current), []);

  if (!user || photos.length === 0) return null;

  return (
    <section className="minner">
      <div className="minner-head">
        <div>
          <h2 className="minner-title">Ferske minner</h2>
          <p className="minner-sub">Bilder fra de siste månedene</p>
        </div>
        <div className="minner-nav">
          <button type="button" className="nav-icon-btn" onClick={() => step(-1)} aria-label="Forrige bilde"><FiChevronLeft /></button>
          <button type="button" className="nav-icon-btn" onClick={() => step(1)} aria-label="Neste bilde"><FiChevronRight /></button>
        </div>
      </div>

      <div
        className="minner-track"
        ref={trackRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        onTouchStart={pauseForTouch}
      >
        {slides.map(({ url, event }, i) => {
          const { emoji, label, color } = typeInfo(event.event_type);
          const to = event.event_type === BLATUR_TYPE ? `/blatur/${event.id}` : `/bilder?event=${encodeURIComponent(event.id)}`;
          return (
            <Link key={`${url}-${i}`} to={to} className="minner-slide" style={{ '--cat': color }} tabIndex={i >= photos.length ? -1 : 0}>
              <SmartImage src={url} size={800} />
              <span className="minner-mark">
                <span className="minner-type">{emoji} {label}</span>
                <span className="minner-place">{place(event)}</span>
                <span className="minner-when">{event.Kvartal}</span>
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export default FerskeMinner;
